import React, { useCallback, useRef } from 'react';
import { Minus, Square, X, Copy } from 'lucide-react';

// PanelWindow — the draggable/resizable chrome for one MDI panel. Pure
// presentation + pointer-event wiring; has no knowledge of which module is
// inside it (that's PanelHost, a later PR) or of the other open panels
// (that's PanelDesktop, in this same file's directory). See the project
// plan (mutable-tinkering-kernighan, TALIT PMS: MDI Dashboard) for the full
// design writeup — this is PR 2/3, built and visually sanity-checked in
// isolation before Dashboard.js wires any of this in (PR 3).
//
// Built on native pointer events, not a drag/resize library — see the
// plan's "Panel shell — built from scratch" section for the full
// reasoning (no existing windowing dependency in this app; the custom
// TALIT-styled chrome is mandatory work regardless of where drag/resize
// math comes from; usePanelManager must stay the single source of truth
// for position/size, which fights a library's own internal state
// ownership when panels are also moved programmatically — restore from
// localStorage, un-minimize, bring-to-front).
//
// MOBILE / chromeEnabled: when false, drag/resize interactions are
// disabled and the panel fills its bounds — same component tree as
// desktop, not a parallel render path (per the plan's mobile-gating
// decision). Only the close button remains meaningful below the
// breakpoint; PanelDesktop (not this file) decides which single panel to
// show in that mode.

const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function PanelWindow({
  panel,
  bounds, // { width, height } of the content region panels are confined to
  zIndex,
  isFront,
  chromeEnabled = true,
  Icon,
  onFocus,       // (id) => void — bring to front, called on any pointerdown inside the panel
  onMove,        // (id, position) => void
  onResize,      // (id, size) => void
  onMinimize,    // (id) => void
  onToggleMaximize, // (id) => void
  onClose,       // (id) => void
  onNewWindow,   // (moduleKey) => void — "+ New Window" button
  children,
}) {
  const dragStateRef = useRef(null);
  const resizeStateRef = useRef(null);

  // A floating window is drawn no bigger than the desktop area and always
  // inside it. A saved size from a larger screen (or a window dragged half
  // off) used to run underneath the taskbar and cover it. Only the drawing is
  // fitted: the saved size and position stay as they were, so the window
  // returns to its full size on a bigger screen.
  const fitted = panel.maximized || !chromeEnabled
    ? null
    : {
        width: Math.min(panel.size.width, bounds.width || panel.size.width),
        height: Math.min(panel.size.height, bounds.height || panel.size.height),
      };
  const effectiveSize = fitted || bounds;
  const effectivePosition = !fitted
    ? { x: 0, y: 0 }
    : {
        x: Math.max(0, Math.min(panel.position.x, (bounds.width || Infinity) - fitted.width)),
        y: Math.max(0, Math.min(panel.position.y, (bounds.height || Infinity) - fitted.height)),
      };

  const handleTitleBarPointerDown = useCallback((e) => {
    onFocus(panel.id);
    if (!chromeEnabled || panel.maximized) return;
    // Only the primary button drags; a right-click or middle-click on the
    // title bar shouldn't start a drag.
    if (e.button !== 0) return;
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: panel.position.x,
      originY: panel.position.y,
    };
    const handlePointerMove = (moveEvent) => {
      const s = dragStateRef.current;
      if (!s) return;
      const dx = moveEvent.clientX - s.startX;
      const dy = moveEvent.clientY - s.startY;
      const nextX = clamp(s.originX + dx, 0, Math.max(0, bounds.width - panel.size.width));
      const nextY = clamp(s.originY + dy, 0, Math.max(0, bounds.height - panel.size.height));
      onMove(panel.id, { x: nextX, y: nextY });
    };
    const handlePointerUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [chromeEnabled, panel.id, panel.maximized, panel.position, panel.size, bounds, onFocus, onMove]);

  const handleResizePointerDown = useCallback((corner) => (e) => {
    e.stopPropagation();
    onFocus(panel.id);
    if (!chromeEnabled || panel.maximized) return;
    resizeStateRef.current = {
      corner,
      startX: e.clientX,
      startY: e.clientY,
      originX: panel.position.x,
      originY: panel.position.y,
      originWidth: panel.size.width,
      originHeight: panel.size.height,
    };
    const handlePointerMove = (moveEvent) => {
      const s = resizeStateRef.current;
      if (!s) return;
      const dx = moveEvent.clientX - s.startX;
      const dy = moveEvent.clientY - s.startY;
      let { originX: x, originY: y, originWidth: width, originHeight: height } = s;

      if (corner.includes('e')) width = clamp(s.originWidth + dx, MIN_WIDTH, bounds.width - x);
      if (corner.includes('s')) height = clamp(s.originHeight + dy, MIN_HEIGHT, bounds.height - y);
      if (corner.includes('w')) {
        const proposedWidth = clamp(s.originWidth - dx, MIN_WIDTH, s.originX + s.originWidth);
        x = s.originX + (s.originWidth - proposedWidth);
        width = proposedWidth;
      }
      if (corner.includes('n')) {
        const proposedHeight = clamp(s.originHeight - dy, MIN_HEIGHT, s.originY + s.originHeight);
        y = s.originY + (s.originHeight - proposedHeight);
        height = proposedHeight;
      }

      if (x !== panel.position.x || y !== panel.position.y) onMove(panel.id, { x, y });
      onResize(panel.id, { width, height });
    };
    const handlePointerUp = () => {
      resizeStateRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [chromeEnabled, panel.id, panel.maximized, panel.position, panel.size, bounds, onFocus, onMove, onResize]);

  const resizeHandles = chromeEnabled && !panel.maximized ? (
    <>
      <div onPointerDown={handleResizePointerDown('n')} className="absolute top-0 left-2 right-2 h-1.5 cursor-ns-resize" />
      <div onPointerDown={handleResizePointerDown('s')} className="absolute bottom-0 left-2 right-2 h-1.5 cursor-ns-resize" />
      <div onPointerDown={handleResizePointerDown('w')} className="absolute left-0 top-2 bottom-2 w-1.5 cursor-ew-resize" />
      <div onPointerDown={handleResizePointerDown('e')} className="absolute right-0 top-2 bottom-2 w-1.5 cursor-ew-resize" />
      <div onPointerDown={handleResizePointerDown('nw')} className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize" />
      <div onPointerDown={handleResizePointerDown('ne')} className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize" />
      <div onPointerDown={handleResizePointerDown('sw')} className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize" />
      <div onPointerDown={handleResizePointerDown('se')} className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize" />
    </>
  ) : null;

  return (
    <div
      className="absolute rounded-md shadow-xl border border-gray-300 bg-white flex flex-col overflow-hidden"
      style={{
        left: effectivePosition.x,
        top: effectivePosition.y,
        width: effectiveSize.width,
        height: effectiveSize.height,
        zIndex,
      }}
      onPointerDownCapture={() => onFocus(panel.id)}
    >
      <div
        onPointerDown={handleTitleBarPointerDown}
        className={`flex items-center gap-2 px-3 py-2 select-none flex-shrink-0 ${
          chromeEnabled && !panel.maximized ? 'cursor-move' : ''
        } ${isFront ? 'bg-shell-dark text-shell-text' : 'bg-shell text-shell-text/80'}`}
      >
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
        <span className="flex-1 text-sm font-medium truncate">{panel.title}</span>
        {onNewWindow && (
          <button
            type="button"
            title="Open another independent window of this module"
            onClick={(e) => { e.stopPropagation(); onNewWindow(panel.moduleKey); }}
            className="p-1 rounded hover:bg-white/20 flex-shrink-0"
          >
            <Copy className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
        {chromeEnabled && (
          <button
            type="button"
            title="Minimize"
            onClick={(e) => { e.stopPropagation(); onMinimize(panel.id); }}
            className="p-1 rounded hover:bg-white/20 flex-shrink-0"
          >
            <Minus className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
        {chromeEnabled && (
          <button
            type="button"
            title={panel.maximized ? 'Restore' : 'Maximize'}
            onClick={(e) => { e.stopPropagation(); onToggleMaximize(panel.id); }}
            className="p-1 rounded hover:bg-white/20 flex-shrink-0"
          >
            <Square className="w-3 h-3" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          title="Close"
          onClick={(e) => { e.stopPropagation(); onClose(panel.id); }}
          className="p-1 rounded hover:bg-red-500/80 flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
        {children}
      </div>
      {resizeHandles}
    </div>
  );
}

export default PanelWindow;
